import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Clock } from 'lucide-react';
import logger from '@/lib/logger';

const cellColors = {
    present: 'bg-emerald-100 dark:bg-emerald-900/40 border-emerald-300 dark:border-emerald-700 hover:bg-emerald-200',
    half_day: 'bg-amber-100 dark:bg-amber-900/40 border-amber-300 dark:border-amber-700 hover:bg-amber-200',
    absent: 'bg-red-100 dark:bg-red-900/40 border-red-300 dark:border-red-700 hover:bg-red-200',
    on_leave: 'bg-blue-100 dark:bg-blue-900/40 border-blue-300 dark:border-blue-700',
    holiday: 'bg-purple-100 dark:bg-purple-900/40 border-purple-300 dark:border-purple-700',
    weekend: 'bg-slate-50 dark:bg-slate-900/40 border-slate-200 dark:border-slate-800 opacity-50',
    upcoming: 'bg-slate-50/50 dark:bg-slate-900/20 border-slate-200 dark:border-slate-800 opacity-30',
    no_data: 'bg-orange-50 dark:bg-orange-900/30 border-orange-300 dark:border-orange-700 hover:bg-orange-100',
};

const dotColors = {
    present: 'bg-emerald-500', half_day: 'bg-amber-500', absent: 'bg-red-500',
    on_leave: 'bg-blue-500', holiday: 'bg-purple-500', weekend: 'bg-slate-300',
    upcoming: 'bg-slate-200', no_data: 'bg-orange-400',
};

const legendItems = [
    { label: 'Present', color: 'bg-emerald-500' }, { label: 'Late', color: 'bg-amber-500' },
    { label: 'Half Day', color: 'bg-amber-500' }, { label: 'Absent', color: 'bg-red-500' },
    { label: 'On Leave', color: 'bg-blue-500' }, { label: 'Holiday', color: 'bg-purple-500' },
    { label: 'No Record', color: 'bg-orange-400' }, { label: 'Off Day', color: 'bg-slate-300' },
];

export const AttendanceCalendarTab = ({ onRecordClick }) => {
    const [selectedMonth, setSelectedMonth] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    const [monthlyAttendance, setMonthlyAttendance] = useState(null);

    const fetchMonthlyAttendance = useCallback(async () => {
        try {
            const [y, m] = selectedMonth.split('-');
            const res = await apiClient.get(`/hr/my-monthly-attendance?year=${y}&month=${m}`);
            setMonthlyAttendance(res.data);
        } catch (e) { logger.warn('Monthly attendance error', e); }
    }, [selectedMonth]);

    useEffect(() => { fetchMonthlyAttendance(); }, [fetchMonthlyAttendance]);

    const handleClick = (day) => {
        if (!day.is_weekend && !day.is_future && day.status !== 'holiday') {
            onRecordClick({ date: day.date, biometric_in: day.biometric_in, biometric_out: day.biometric_out });
        }
    };

    const renderCalendar = () => {
        if (!monthlyAttendance?.days?.length) {
            return (
                <div className="text-center py-8 text-muted-foreground">
                    <Clock className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No attendance data for this month</p>
                </div>
            );
        }
        const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        const firstDayName = monthlyAttendance.days[0]?.day_name;
        const startOffset = dayNames.indexOf(firstDayName);
        const padded = [...Array(startOffset >= 0 ? startOffset : 0).fill(null), ...monthlyAttendance.days];
        const rows = [];
        for (let i = 0; i < padded.length; i += 7) rows.push(padded.slice(i, i + 7));

        return (
            <div className="space-y-1">
                {rows.map((row, ri) => (
                    <div key={ri} className="grid grid-cols-7 gap-1">
                        {row.map((day, ci) => {
                            if (!day) return <div key={`empty-${ci}`} className="min-h-[72px]" />;
                            const isClickable = !day.is_weekend && !day.is_future && day.status !== 'holiday';
                            return (
                                <div key={day.date} onClick={() => isClickable && handleClick(day)} className={`min-h-[72px] p-1.5 rounded-lg border text-center transition-all ${isClickable ? 'cursor-pointer' : 'cursor-default'} ${cellColors[day.status] || 'border-slate-200'}`} data-testid={`cal-${day.date}`}>
                                    <div className="flex items-center justify-between mb-0.5">
                                        <span className={`text-xs font-bold ${day.is_weekend ? 'text-red-400' : ''}`}>{day.day}</span>
                                        <span className={`w-2 h-2 rounded-full ${dotColors[day.status] || ''}`} />
                                    </div>
                                    {!day.is_weekend && !day.is_future && day.status !== 'holiday' && (
                                        <div className="text-[9px] space-y-0.5">
                                            {day.biometric_in && <p className="font-mono text-foreground">{day.biometric_in?.slice(0,5)}</p>}
                                            {day.total_work_hours != null && <p className="text-muted-foreground">{day.total_work_hours}h</p>}
                                            {day.late_minutes > 0 && <p className="text-amber-600 font-medium">Late {day.late_minutes}m</p>}
                                            {!day.biometric_in && day.status === 'no_data' && <p className="text-orange-500">No punch</p>}
                                        </div>
                                    )}
                                    {day.status === 'holiday' && <p className="text-[8px] text-purple-500 mt-1 truncate">{day.holiday_name}</p>}
                                    {day.status === 'on_leave' && <p className="text-[8px] text-blue-500 mt-1">{day.leave_type?.replace(/_/g,' ')}</p>}
                                    {day.status === 'weekend' && <p className="text-[8px] text-slate-400 mt-1">Off</p>}
                                    {day.special_period && <p className="text-[7px] text-purple-400 truncate">{day.special_period}</p>}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className="px-3 py-2 rounded-md border bg-background text-sm" data-testid="attendance-month-picker" />
                    {monthlyAttendance?.shift && (
                        <Badge variant="outline" className="text-xs">{monthlyAttendance.shift.name}: {monthlyAttendance.shift.start} - {monthlyAttendance.shift.end}</Badge>
                    )}
                </div>
            </div>

            {monthlyAttendance?.summary && (
                <div className="grid grid-cols-3 sm:grid-cols-7 gap-2">
                    {[
                        { label: 'Present', value: monthlyAttendance.summary.present, color: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-950' },
                        { label: 'Absent', value: monthlyAttendance.summary.absent, color: 'text-red-600 bg-red-50 dark:bg-red-950' },
                        { label: 'Half Day', value: monthlyAttendance.summary.half_day, color: 'text-amber-600 bg-amber-50 dark:bg-amber-950' },
                        { label: 'On Leave', value: monthlyAttendance.summary.on_leave, color: 'text-blue-600 bg-blue-50 dark:bg-blue-950' },
                        { label: 'Holiday', value: monthlyAttendance.summary.holiday || 0, color: 'text-purple-600 bg-purple-50 dark:bg-purple-950' },
                        { label: 'Late', value: monthlyAttendance.summary.late, color: 'text-orange-600 bg-orange-50 dark:bg-orange-950' },
                        { label: 'No Data', value: monthlyAttendance.summary.no_data, color: 'text-slate-500 bg-slate-50 dark:bg-slate-900' },
                    ].map(s => (
                        <div key={s.label} className={`p-2.5 rounded-lg text-center ${s.color}`}>
                            <p className="text-lg font-bold">{s.value}</p>
                            <p className="text-[10px] font-medium uppercase tracking-wider">{s.label}</p>
                        </div>
                    ))}
                </div>
            )}

            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base">Monthly Calendar</CardTitle>
                    <CardDescription>Click any working day to view details or apply for regularization</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-7 gap-1 mb-1">
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
                            <div key={d} className={`text-center text-[10px] font-semibold uppercase tracking-wider py-1 ${d === 'Sun' ? 'text-red-400' : 'text-muted-foreground'}`}>{d}</div>
                        ))}
                    </div>
                    {renderCalendar()}
                    <div className="flex flex-wrap gap-3 mt-4 pt-3 border-t">
                        {legendItems.map(l => (
                            <div key={l.label} className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                                <span className={`w-2.5 h-2.5 rounded-full ${l.color}`} />
                                {l.label}
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};
